import { Trans } from '@lingui/macro';
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
} from '@reach/accordion';
import { Button } from '@we-make-websites/ui-lib';
import { useClearRefinements } from 'react-instantsearch';
import { useMediaMatch } from 'rooks';
import { useContext } from 'use-context-selector';
import IconChevronDown from 'icons/direction/chevron/down.svg';
import type { StoreFilterSetting } from '../globalContentContext/GlobalContentContext';
import Overlay from '../overlay/Overlay';
import { OverlayContext } from '../overlay/OverlayContext';
import { ActiveFilters } from './ActiveFilters';
import { CustomRefinementList } from './CustomRefinementList';
import styles from './MobileFiltersOverlay.module.scss';

export type MobileFiltersOverlayProps = {
  namespace: string;
  filters: StoreFilterSetting[];
};

export const MobileFiltersOverlay = ({
  namespace,
  filters = [],
}: MobileFiltersOverlayProps) => {
  const { close } = useContext(OverlayContext);

  const isDesktop = useMediaMatch('(min-width: 1024px)');

  if (isDesktop) {
    close(namespace);
  }

  const footer = (
    <MobileFiltersOverlayFooter onApply={() => close(namespace)} />
  );

  /**
   * Custom handle closing the overlay on route change.
   */
  const customHandleRouteChange = (
    url: string,
    { shallow }: { shallow?: boolean }
  ) => {
    /* Don't close on shallow route change. */
    if (shallow) {
      return;
    }
    close(namespace);
  };

  return (
    <Overlay
      type="mobile-drawer"
      blockClass="mobileFiltersOverlay"
      namespace={namespace}
      direction="up"
      footer={footer}
      useReachUi={false}
      customHandleRouteChange={customHandleRouteChange}
    >
      <div className={styles.mobileFiltersOverlay__activeFiltersContainer}>
        <ActiveFilters
          showClearRefinements={false}
          className={styles.mobileFiltersOverlay__activeFilters}
        />
      </div>
      <div className={styles.mobileFiltersOverlay__filterContainer}>
        <Accordion collapsible>
          {filters.map((filter) => (
            <AccordionItem
              key={filter.attribute}
              className={styles.mobileFiltersOverlay__accordionItem}
            >
              <AccordionButton
                className={styles.mobileFiltersOverlay__filterTitle}
              >
                <span className="text-utility-utility-mobile">
                  {filter.label}
                </span>
                <IconChevronDown />
              </AccordionButton>
              <AccordionPanel>
                <CustomRefinementList
                  attribute={filter.attribute}
                  classes={{
                    container: styles.mobileCustomRefinements,
                    list: styles.mobileCustomRefinements__list,
                    item: styles.mobileCustomRefinements__item,
                  }}
                  limit={50}
                  sortBy={['count:desc', 'name:asc']}
                  filter={filter}
                />
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </Overlay>
  );
};

const MobileFiltersOverlayFooter = ({ onApply }: { onApply: () => void }) => {
  const { refine } = useClearRefinements();
  return (
    <div className={styles.mobileFiltersOverlay__footer}>
      <Button modifiers="block" onClick={onApply}>
        <Trans id="filters.viewResults">View Results</Trans>
      </Button>
      <Button modifiers="outlined block" onClick={() => refine()}>
        <Trans id="filters.clearAll">Clear All</Trans>
      </Button>
    </div>
  );
};
